import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { businessToday } from '../../utils/time';

/**
 * The standards every yard is judged on, at the top of the crew's day.
 * Same list for every stop; the owner scores random yards against it.
 *
 * Copy is the owner's, word for word (Sept 15 2026) — do not soften or add.
 * It is a reminder, not a gate: nothing here has to be tapped to start or
 * finish a job. Collapsing lasts for the day; tomorrow it opens again.
 */
const SECTIONS: { role: string; lines: string[] }[] = [
  { role: 'Mower', lines: [
    'All mowable grass is cleanly cut. No missed spots. No obvious hiccups. No torn-up grass (mower did not chew or tear the lawn).',
    'Scalp or wrong height: photo/text Michael before the client does.',
    'Mower hits something: same — Michael first.',
    'No tire marks on the driveway or sidewalk.',
  ] },
  { role: 'Weed-eater', lines: [
    'Edges are sharp and clean.',
    'Everything the mower cannot reach is weed-eaten.',
    'Weed-eater height matches the mow height. Not too low, not too high.',
  ] },
  { role: 'Blower', lines: [
    'Grass blown off the street, sidewalk, driveway, and both porches — back onto the lawn.',
    'Clumps from the mower are blown out and spread on the lawn.',
  ] },
  { role: 'Notes', lines: [
    'Job notes are done as written (example: push mow = use the push mower). Extra pay follows the note.',
  ] },
];

function storageKey() { return `kam_judged_collapsed_${businessToday()}`; }

export default function JudgedOnThis() {
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(storageKey()) !== '1'; } catch { return true; }
  });
  const toggle = () => {
    setOpen(o => {
      try { if (o) localStorage.setItem(storageKey(), '1'); else localStorage.removeItem(storageKey()); } catch { /* fine */ }
      return !o;
    });
  };
  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50/60">
      <button type="button" onClick={toggle} className="w-full flex items-center justify-between px-4 py-2.5 text-left">
        <span>
          <span className="block text-sm font-bold text-brand-900">Keep Austin Mowed — judged on this</span>
          <span className="block text-[11px] text-brand-700">You are judged on this</span>
        </span>
        <ChevronDown className={`h-4 w-4 text-brand-700 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
          {SECTIONS.map(s => (
            <div key={s.role}>
              <p className="text-xs font-bold text-brand-900 uppercase tracking-wide">{s.role}</p>
              <ul className="mt-0.5 space-y-0.5">
                {s.lines.map((l, i) => <li key={i} className="text-xs text-gray-800 leading-snug pl-3 relative before:content-['•'] before:absolute before:left-0">{l}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
